import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View, type ViewProps } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button, DisplayText, IllusDressing_1 } from '@garnish/core';
import { Image } from '@garnish/media';

import { PageWrapper } from '../PageWrapper';

// ────────────────────────────────────────────────────────────────────────────────

export const NotFoundViewWrapper = (props: WrapperProps) => {
  const { withoutFooter, children } = props;

  return (
    <PageWrapper
      contentContainerStyle={styles.container}
      safeAreaEdges={['top']}
      withoutFooter={withoutFooter}
    >
      <View testID="not-found-view" style={styles.wrapper}>
        {children}
      </View>
    </PageWrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const NotFoundViewImage = (props: ImageProps) => {
  const { illustration } = props;

  return <Image source={illustration ?? IllusDressing_1} style={styles.img} contentFit="contain" />;
};

// ────────────────────────────────────────────────────────────────────────────────

export const NotFoundViewTitle = (props: TitleProps) => {
  const { title, alignment = 'center', sizeMatch = ['24', '32', '40', '40'] } = props;
  const { formatMessage } = useIntl();

  const titleStyles = [
    styles.heading,
    alignment === 'center' ? styles.textCenter : styles.textLeft,
  ];

  return (
    <DisplayText
      sizeMatch={sizeMatch}
      style={titleStyles}
      accessibilityRole="header"
      {...{ accessibilityLevel: 1 }}
    >
      {title ?? formatMessage(messages.notFoundViewHeading)}
    </DisplayText>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const NotFoundViewDescription = (props: DescriptionProps) => {
  const { description, alignment = 'left', sizeMatch = ['16'] } = props;
  const { formatMessage } = useIntl();

  const descriptionStyles = alignment === 'center' ? styles.textCenter : styles.textLeft;

  return (
    <BodyText sizeMatch={sizeMatch} style={descriptionStyles}>
      {description ?? formatMessage(messages.notFoundViewText)}
    </BodyText>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const NotFoundViewCta = (props: CtaProps) => {
  const { btnLabel, onBtnPress } = props;

  const { formatMessage } = useIntl();
  const router = useRouter();

  const handlePressHome = useCallback(() => {
    router.dismissTo('/');
  }, [router]);

  return (
    <View style={styles.btnContainer}>
      <Button
        testID="not-found-view.btn"
        palette="primary"
        size="large"
        style={styles.btn}
        onPress={onBtnPress ?? handlePressHome}
      >
        {btnLabel ?? formatMessage(messages.notFoundViewReturnHome)}
      </Button>
    </View>
  );
};

export const NotFoundViewCtaV2 = (props: CtaProps) => {
  const { btnLabel, onBtnPress } = props;
  const { formatMessage } = useIntl();
  const router = useRouter();

  const navigateToHome = useCallback(() => {
    router.dismissTo('/');
  }, [router]);

  return (
    <View style={styles.btnContainer}>
      <Button
        testID="not-found-view.btn"
        palette="primary"
        size="large"
        style={styles.btn}
        onPress={onBtnPress ?? navigateToHome}
      >
        {btnLabel ?? formatMessage(messages.notFoundViewReturnHome)}
      </Button>
    </View>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  wrapper: {
    flex: 1,
    paddingVertical: theme.spacing['10'],
    paddingHorizontal: theme.spacing['6'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  img: {
    width: 200,
    height: 200,
  },
  heading: {
    marginVertical: theme.spacing['4'],
    textAlign: 'center',
  },
  btnContainer: {
    paddingTop: theme.spacing['10'],
  },
  btn: {
    minWidth: 193,
  },
  textLeft: {
    textAlign: 'left',
  },
  textCenter: {
    textAlign: 'center',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  notFoundViewHeading: {
    defaultMessage: 'Oops! Nothing to see here.',
    description: 'Not Found View | Heading',
  },
  notFoundViewText: {
    defaultMessage: 'We ran into an issue. Let’s get you back to the good stuff.',
    description: 'Not Found View | Text',
  },
  notFoundViewReturnHome: {
    defaultMessage: 'Return home',
    description: 'Not Found View | Return Home',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────────

type WrapperProps = Readonly<{
  withoutFooter?: boolean;
  children: ViewProps['children'];
}>;

type ImageProps = Readonly<{
  illustration?: ComponentProps<typeof Image>['source'];
}>;

type TitleProps = Readonly<{
  title?: string;
  sizeMatch?: ComponentProps<typeof DisplayText>['sizeMatch'];
  alignment?: 'left' | 'center';
}>;

type DescriptionProps = Readonly<{
  description?: string;
  sizeMatch?: ComponentProps<typeof BodyText>['sizeMatch'];
  alignment?: 'left' | 'center';
}>;

type CtaProps = Readonly<{
  btnLabel?: string;
  onBtnPress?: () => void;
}>;
